'use client';

import { createContext, useContext } from 'react';
import type { AuthModalState } from '@/types/auth-types';

export interface OpenAuthModalOptions {
  /**
   * Body state the modal opens in. Defaults to the sign-in form. Only
   * `AuthModalState.Initial` is treated as a sign-in gate, so only that state
   * is auto-dismissed once the user authenticates.
   */
  initialState?: AuthModalState;
  /**
   * Runs when the user closes the modal. Not called when a sign-in gate
   * auto-dismisses because the session landed — that is a success, not a
   * dismissal, and callers resume their intent off the auth state instead.
   */
  onDismiss?: () => void;
}

export interface AuthModalContextType {
  openAuthModal: (options?: OpenAuthModalOptions) => void;
  closeAuthModal: () => void;
  /**
   * True while the single shared modal is on screen, whoever opened it.
   * Callers holding a gated action read this to tell "still deciding" from
   * "dismissed without signing in", so a much later unrelated login cannot
   * replay a stale intent.
   */
  isAuthModalOpen: boolean;
}

export const AuthModalContext = createContext<AuthModalContextType | null>(null);

export function useAuthModal(): AuthModalContextType {
  const context = useContext(AuthModalContext);
  if (!context) {
    throw new Error('useAuthModal must be used within an AuthModalProvider');
  }
  return context;
}
